import type { FormEvent, JSX } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Badge, Button, Checkbox, Dialog, Input, useToast } from '@artosera/ui';
import { usePeut } from '../../../auth/usePeut.js';
import { useElevation } from '../../../components/useElevation.js';
import { formaterDate } from '../../../lib/format.js';
import {
  DUREE_DEMANDE_MAX_JOURS,
  MOTIF_MIN,
  NATURES_LOT,
  demanderMandat,
  obtenirMandat,
  poserMandat,
  retirerMandat,
  type MandatVue,
  type NatureLot,
} from '../../../lib/reprise.js';
import pageStyles from '../../../styles/page.module.css';
import formStyles from '../../../styles/form.module.css';
import { jourLocal, messageReprise } from './messages.js';
import styles from './Reprise.module.css';

type Formulaire = 'demande' | 'pose' | 'revocation' | 'retrait' | null;

export function cleMandat(idGalerie: string): readonly string[] {
  return ['console', 'galerie', idGalerie, 'reprise', 'mandat'];
}

/**
 * Le mandat de reprise (spec § 4) : ce que la galerie a autorisé, jusqu'à
 * quand, et les gestes de la console — demander à la galerie, poser le mandat
 * du contrat (direction de Venio, second facteur récent), retirer.
 */
export function MandatBloc({ idGalerie }: { idGalerie: string }): JSX.Element {
  const { t } = useTranslation('console');
  const peut = usePeut();
  const peutDemander = peut('console:run-import');
  const peutGerer = peut('console:manage-mandate');
  const client = useQueryClient();
  const { toast } = useToast();
  const elevation = useElevation();
  const [ouvert, setOuvert] = useState<Formulaire>(null);
  const [erreur, setErreur] = useState<string | null>(null);

  const [natures, setNatures] = useState<NatureLot[]>([]);
  const [confidentiels, setConfidentiels] = useState(false);
  const [motif, setMotif] = useState('');
  const [dureeJours, setDureeJours] = useState('14');
  const [echeance, setEcheance] = useState(() => jourLocal(60));
  const [reference, setReference] = useState('');

  const etat = useQuery({
    queryKey: cleMandat(idGalerie),
    queryFn: () => obtenirMandat(idGalerie),
  });
  const { actif, demande } = etat.data ?? { actif: null, demande: null };

  const relire = (): Promise<void> =>
    client.invalidateQueries({ queryKey: cleMandat(idGalerie) }).then(() => undefined);

  const fermer = (): void => {
    setOuvert(null);
    setErreur(null);
  };

  const ouvrir = (formulaire: Formulaire): void => {
    setErreur(null);
    setNatures([]);
    setConfidentiels(false);
    setMotif('');
    setDureeJours('14');
    setEcheance(jourLocal(60));
    setReference('');
    setOuvert(formulaire);
  };

  const finir = (titre: string): void => {
    toast({ title: titre, closeLabel: t('fiche.domaines.fermer') });
    fermer();
    void relire();
  };
  const echec = (cause: unknown): void => setErreur(messageReprise(t, cause));

  const demander = useMutation({
    mutationFn: () =>
      demanderMandat(idGalerie, {
        natures,
        confidentiels: natures.includes('documents') && confidentiels,
        motif: motif.trim(),
        dureeJours: Number(dureeJours),
      }),
    onSuccess: () => finir(t('reprise.mandat.demandeOk')),
    onError: echec,
  });

  const poser = useMutation({
    mutationFn: () =>
      elevation.executer(() =>
        poserMandat(idGalerie, {
          natures,
          confidentiels: natures.includes('documents') && confidentiels,
          expireLe: echeance,
          reference: reference.trim(),
          motif: motif.trim(),
        }),
      ),
    onSuccess: () => finir(t('reprise.mandat.poseOk')),
    onError: echec,
  });

  const retirer = useMutation({
    mutationFn: () => retirerMandat(idGalerie),
    onSuccess: () => finir(t(actif ? 'reprise.mandat.revoqueOk' : 'reprise.mandat.retireOk')),
    onError: echec,
  });

  const duree = Number(dureeJours);
  const dureeValide = Number.isInteger(duree) && duree >= 1 && duree <= DUREE_DEMANDE_MAX_JOURS;
  const motifValide = motif.trim().length >= MOTIF_MIN;
  const demandeValide = natures.length > 0 && motifValide && dureeValide;
  const poseValide =
    natures.length > 0 &&
    motifValide &&
    reference.trim() !== '' &&
    echeance > jourLocal(0) &&
    echeance <= jourLocal(365);

  const basculer = (nature: NatureLot, coche: boolean): void =>
    setNatures((courantes) =>
      coche ? [...courantes, nature] : courantes.filter((autre) => autre !== nature),
    );

  const champsCommuns = (suffixe: string): JSX.Element => (
    <>
      <fieldset className={styles.natures}>
        <legend>{t('reprise.mandat.champNatures')}</legend>
        {NATURES_LOT.map((nature) => (
          <Checkbox
            key={nature}
            id={`mandat-${suffixe}-${nature}`}
            label={t(`reprise.natures.${nature}`)}
            checked={natures.includes(nature)}
            onCheckedChange={(coche) => basculer(nature, coche === true)}
          />
        ))}
      </fieldset>
      {natures.includes('documents') ? (
        <Checkbox
          id={`mandat-${suffixe}-confidentiels`}
          label={t('reprise.mandat.champConfidentiels')}
          checked={confidentiels}
          onCheckedChange={(coche) => setConfidentiels(coche === true)}
        />
      ) : null}
      <div className={formStyles.champ}>
        <label htmlFor={`mandat-${suffixe}-motif`}>{t('reprise.mandat.champMotif')}</label>
        <Input
          id={`mandat-${suffixe}-motif`}
          value={motif}
          maxLength={500}
          onChange={(e) => setMotif(e.target.value)}
        />
        <span className={formStyles.aide}>{t('reprise.mandat.aideMotif')}</span>
      </div>
    </>
  );

  const soumettre = (evenement: FormEvent, action: () => void, valide: boolean): void => {
    evenement.preventDefault();
    if (valide) action();
  };

  const formulaireOuvert = ouvert === 'demande' || ouvert === 'pose';

  return (
    <section className={pageStyles.panneau} aria-labelledby="reprise-mandat">
      <div className={pageStyles.sectionTitre}>
        <h2 id="reprise-mandat">{t('reprise.mandat.titre')}</h2>
      </div>
      <div className={styles.pile}>
        <p className={styles.texte}>{t('reprise.mandat.texte')}</p>

        {etat.isLoading ? <p className={pageStyles.videEtat}>{t('reprise.chargement')}</p> : null}
        {etat.isError ? (
          <p className={styles.erreur} role="alert">
            {messageReprise(t, etat.error)}
          </p>
        ) : null}

        {actif ? (
          <MandatCarte mandat={actif} etat="actif" />
        ) : etat.isSuccess && !demande ? (
          <p className={styles.avertissement} role="note">
            {t('reprise.mandat.aucun')}
          </p>
        ) : null}
        {demande ? <MandatCarte mandat={demande} etat="en_attente" /> : null}

        <div className={styles.actions}>
          {peutDemander && !demande ? (
            <Button variant="secondary" onClick={() => ouvrir('demande')}>
              {t('reprise.mandat.demander')}
            </Button>
          ) : null}
          {peutGerer ? (
            <Button variant="secondary" onClick={() => ouvrir('pose')}>
              {t('reprise.mandat.poser')}
            </Button>
          ) : null}
          {peutGerer && actif ? (
            <Button variant="danger" onClick={() => ouvrir('revocation')}>
              {t('reprise.mandat.revoquer')}
            </Button>
          ) : null}
          {peutGerer && !actif && demande ? (
            <Button variant="ghost" onClick={() => ouvrir('retrait')}>
              {t('reprise.mandat.retirerDemande')}
            </Button>
          ) : null}
        </div>
      </div>

      <Dialog
        open={formulaireOuvert && !elevation.enElevation}
        onOpenChange={(estOuvert) => {
          if (!estOuvert) fermer();
        }}
        title={t(ouvert === 'pose' ? 'reprise.mandat.poseTitre' : 'reprise.mandat.demandeTitre')}
        description={t(
          ouvert === 'pose' ? 'reprise.mandat.poseAide' : 'reprise.mandat.demandeAide',
        )}
        closeLabel={t('reprise.mandat.annuler')}
        footer={
          <>
            <Button variant="ghost" onClick={fermer}>
              {t('reprise.mandat.annuler')}
            </Button>
            {ouvert === 'pose' ? (
              <Button
                loading={poser.isPending}
                disabled={!poseValide}
                onClick={() => poser.mutate()}
              >
                {t('reprise.mandat.poserAction')}
              </Button>
            ) : (
              <Button
                loading={demander.isPending}
                disabled={!demandeValide}
                onClick={() => demander.mutate()}
              >
                {t('reprise.mandat.envoyer')}
              </Button>
            )}
          </>
        }
      >
        <form
          className={styles.pile}
          noValidate
          onSubmit={(e) =>
            ouvert === 'pose'
              ? soumettre(e, () => poser.mutate(), poseValide)
              : soumettre(e, () => demander.mutate(), demandeValide)
          }
        >
          {ouvert === 'pose' ? (
            <>
              {champsCommuns('pose')}
              <div className={formStyles.champ}>
                <label htmlFor="mandat-pose-reference">{t('reprise.mandat.champReference')}</label>
                <Input
                  id="mandat-pose-reference"
                  value={reference}
                  maxLength={200}
                  onChange={(e) => setReference(e.target.value)}
                />
              </div>
              <div className={formStyles.champ}>
                <label htmlFor="mandat-pose-echeance">{t('reprise.mandat.champEcheance')}</label>
                <Input
                  id="mandat-pose-echeance"
                  type="date"
                  min={jourLocal(1)}
                  max={jourLocal(365)}
                  value={echeance}
                  onChange={(e) => setEcheance(e.target.value)}
                />
              </div>
            </>
          ) : (
            <>
              {champsCommuns('demande')}
              <div className={formStyles.champ}>
                <label htmlFor="mandat-demande-duree">{t('reprise.mandat.champDuree')}</label>
                <Input
                  id="mandat-demande-duree"
                  type="number"
                  inputMode="numeric"
                  min={1}
                  max={DUREE_DEMANDE_MAX_JOURS}
                  value={dureeJours}
                  onChange={(e) => setDureeJours(e.target.value)}
                />
                <span className={formStyles.aide}>{t('reprise.mandat.aideDuree')}</span>
              </div>
            </>
          )}
          {natures.length === 0 ? (
            <span className={formStyles.aide}>{t('reprise.mandat.naturesRequises')}</span>
          ) : null}
          {erreur ? (
            <p className={styles.erreur} role="alert">
              {erreur}
            </p>
          ) : null}
        </form>
      </Dialog>

      <Dialog
        open={ouvert === 'revocation' || ouvert === 'retrait'}
        onOpenChange={(estOuvert) => {
          if (!estOuvert) fermer();
        }}
        title={t(
          ouvert === 'retrait' ? 'reprise.mandat.retirerTitre' : 'reprise.mandat.revoquerTitre',
        )}
        description={t(
          ouvert === 'retrait' ? 'reprise.mandat.retirerTexte' : 'reprise.mandat.revoquerTexte',
        )}
        closeLabel={t('reprise.mandat.annuler')}
        footer={
          <>
            <Button variant="ghost" onClick={fermer}>
              {t('reprise.mandat.annuler')}
            </Button>
            <Button variant="danger" loading={retirer.isPending} onClick={() => retirer.mutate()}>
              {t(
                ouvert === 'retrait' ? 'reprise.mandat.retirerDemande' : 'reprise.mandat.revoquer',
              )}
            </Button>
          </>
        }
      >
        {erreur ? (
          <p className={styles.erreur} role="alert">
            {erreur}
          </p>
        ) : null}
      </Dialog>
      {elevation.dialogue}
    </section>
  );
}

function MandatCarte({
  mandat,
  etat,
}: {
  mandat: MandatVue;
  etat: 'actif' | 'en_attente';
}): JSX.Element {
  const { t } = useTranslation('console');
  return (
    <article
      className={styles.mandat}
      data-etat={etat}
      aria-label={t(etat === 'actif' ? 'reprise.mandat.actif' : 'reprise.mandat.enAttente')}
    >
      <div className={styles.rangee}>
        <Badge tone={etat === 'actif' ? 'available' : 'reserved'}>
          {t(etat === 'actif' ? 'reprise.mandat.actif' : 'reprise.mandat.enAttente')}
        </Badge>
        {etat === 'actif' ? (
          <span>{t('reprise.mandat.echeance', { date: formaterDate(mandat.expireLe, '—') })}</span>
        ) : null}
      </div>
      <dl className={styles.details}>
        <div>
          <dt>{t('reprise.mandat.natures')}</dt>
          <dd>{mandat.natures.map((nature) => t(`reprise.natures.${nature}`)).join(', ')}</dd>
        </div>
        <div>
          <dt>{t('reprise.mandat.confidentiels')}</dt>
          <dd>{t(mandat.confidentiels ? 'reprise.mandat.inclus' : 'reprise.mandat.exclus')}</dd>
        </div>
      </dl>
      {etat === 'actif' ? (
        <p className={styles.discret}>
          {mandat.source === 'contrat'
            ? t('reprise.mandat.origineContrat', { reference: mandat.reference })
            : t('reprise.mandat.origineGalerie', { email: mandat.accordePar ?? '—' })}
        </p>
      ) : (
        <p className={styles.discret}>
          {t('reprise.mandat.demandeTexte', {
            count: mandat.dureeJours ?? 0,
            email: mandat.demandePar ?? '—',
            date: formaterDate(mandat.expireLe, '—'),
          })}
        </p>
      )}
      <p className={styles.discret}>{t('reprise.mandat.motif', { motif: mandat.motif })}</p>
    </article>
  );
}
